import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Description,
  Medication,
  Science,
  Article,
  ExpandMore,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { DocumentPreviewModal } from "@/fluxvet/components/documents/DocumentPreviewModal";
import { SignedDocumentsList } from "./SignedDocumentsList";
import {
  generateTreatmentDocument,
  generateExamDocument,
  generateReportDocument,
  generateSectionDocument,
} from "@/fluxvet/services/documentGenerator";
import { useVetDocumentInfo } from "@/fluxvet/hooks/useVetDocumentInfo";
import { PostMedicationRequest } from "@/services/fhir/patientsummary/medicationrequest/PostMedicationRequest";
import { PostServiceRequest } from "@/services/fhir/patientsummary/serviceRequest/PostServiceRequest";
import { PostDocumentReference } from "@/services/fhir/patientsummary/documentreference/PostDocumentReference";
import { Base64SignPDF } from "@/services/dsig/Base64SignPDF";
import fluxSignerService from "@/services/fluxsigner/FluxSignerService";
import { useLocation } from "react-router-dom";
import { useSessionContext } from "@/contexts/SessionContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useFhirData } from "@/utils/useFhirData";
import { toast } from "react-toastify";
import { GetAdminQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetAdminQuestionnaireResponse";
import { oids } from "@/configs/Settings";

// =============================================================================
// FUNÇÕES AUXILIARES - Adaptação de dados
// =============================================================================

/**
 * Adapta tratamentos veterinários para o formato esperado pelo MedicationRequest
 * Converte a estrutura simples do formulário veterinário para o formato médico
 *
 * @param treatments - Array de tratamentos do formulário veterinário
 * @returns Array no formato esperado pelo PostMedicationRequest
 */
const adaptTreatmentsToMedicationFormat = (treatments: any[]) => {
  return treatments.map((treatment) => ({
    name: treatment.name, // Nome do medicamento
    note: `${treatment.dosage}${
      treatment.description ? `\n${treatment.description}` : ""
    }`, // Combina dosagem e descrição
    // Campos estruturados podem ser adicionados futuramente se necessário
    // frequency: null,
    // period: null,
    // quantity: null,
  }));
};

/**
 * Adapta exames/procedimentos veterinários para o formato ServiceRequest
 *
 * @param examsOrProcedures - Array de exames do formulário veterinário
 * @returns Array no formato esperado pelo PostServiceRequest
 */
const adaptExamsToServiceRequestFormat = (examsOrProcedures: any[]) => {
  return examsOrProcedures.map((exam) => ({
    name: exam.description,
    note: exam.notes,
    // category pode ser adicionado futuramente
  }));
};

/**
 * Normaliza o base64 da imagem de background para o formato esperado pelo VetDocumentTemplate.
 * Remove o prefixo "data:image/...;base64," se existir, pois o template adiciona esse prefixo.
 *
 * @param value - Base64 bruto ou data URL (ex.: "data:image/png;base64,XXXX")
 * @returns Base64 puro ou undefined se inválido
 */
const normalizeBackgroundBase64 = (
  value: string | undefined
): string | undefined => {
  if (!value || typeof value !== "string") return undefined;
  const match = value.match(/^data:image\/\w+;base64,(.+)$/);
  return match ? match[1] : value;
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente da terceira etapa: Geração e Assinatura de documentos
 *
 * Funcionalidades:
 * - Visualizar todos os itens criados nas etapas anteriores
 * - Selecionar itens individualmente para combinar
 * - Gerar documento individual de cada item
 * - Gerar documento com todos os itens de uma seção
 * - Gerar documento combinado com itens selecionados
 * - Persistir documentos assinados no servidor FHIR
 */
export const SignatureStep: React.FC = () => {
  // Contextos e hooks necessários
  const { user } = useSessionContext();
  const { userPractitioner, userPractitionerRole, userSpecialties } =
    usePractitionerRoleContext();
  const { organization } = useOrganizationContext();
  const { state } = useLocation();
  const references = state?.encounterReference;

  // Busca os dados do formulário do contexto
  const { methods, appointment } = useVetAttendance();
  const treatments = methods.watch("treatments") || [];
  const examsOrProcedures = methods.watch("examsOrProcedures") || [];
  const reports = methods.watch("reports") || [];

  // Hook customizado para centralizar a lógica de informações do documento
  const { petInfo, tutorInfo, clinicInfo } = useVetDocumentInfo();

  // Prepara dados para DocumentReference (mesmo padrão do VetAttendence.tsx)
  const healthcare = useFhirData?.findParticipant(
    appointment?.participant,
    "HealthcareService"
  )?.display;

  const servicePratice = userSpecialties?.find(
    (el: any) => el.coding?.[0]?.display === healthcare
  );

  const docReferenceData = {
    organization: {
      id: organization?.id,
      alias: organization?.alias?.[0],
      name: organization?.name,
    },
    practitioner: {
      id: userPractitioner?.id,
      name: userPractitioner?.name?.[0]?.text,
    },
    servicePratice: {
      display: servicePratice?.coding?.[0]?.display,
      code: servicePratice?.coding?.[0]?.code,
      system: "https://fluxmed.com.br/fhir/CodeSystem/CBO",
    },
    time: {
      start: appointment?.start,
      end: appointment?.end,
    },
  };

  // Estado para controle do modal de visualização (único modal para tudo)
  const [modalOpen, setModalOpen] = useState(false);
  const [documentBase64, setDocumentBase64] = useState<string | null>(null);
  const [documentIdValue, setDocumentIdValue] = useState<string>("");
  const [documentUUID, setDocumentUUID] = useState<string>("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [documentFilename, setDocumentFilename] = useState("");
  const [loading, setLoading] = useState(false);

  // Estado para rastrear o que está sendo assinado
  const [currentDocumentType, setCurrentDocumentType] = useState<string>("");
  const [currentItemIds, setCurrentItemIds] = useState<string[]>([]);

  // Verifica se há algum item disponível (incluindo assinados)
  const hasAnyItems =
    treatments.length > 0 || examsOrProcedures.length > 0 || reports.length > 0;

  // Filtra itens não assinados para evitar dupla assinatura
  const unsignedTreatments = treatments.filter((t: any) => !t.signed);
  const unsignedExams = examsOrProcedures.filter((e: any) => !e.signed);
  const unsignedReports = reports.filter((r: any) => !r.signed);

  // Verifica se há itens não assinados
  const hasUnsignedItems =
    unsignedTreatments.length > 0 ||
    unsignedExams.length > 0 ||
    unsignedReports.length > 0;

  /**
   * Assina o documento digitalmente e persiste no servidor FHIR
   * Fluxo:
   * 1. Assina o PDF usando Base64SignPDF (VidaaS, SafeID ou FluxSigner)
   * 2. Cria MedicationRequest/ServiceRequest (se aplicável)
   * 3. Persiste DocumentReference com o PDF assinado
   *
   * @param provider - Provedor de assinatura (vidaas, safeid ou fluxsigner)
   * @param password - Senha (apenas para SafeID)
   * @param lifeTime - Tempo de sessão em segundos
   * @param certificateAlias - Alias do certificado (apenas para FluxSigner)
   */
  const handleSignDocument = async (
    provider: "vidaas" | "safeid" | "fluxsigner",
    password?: string,
    lifeTime?: number,
    certificateAlias?: string
  ) => {
    try {
      // =====================================================================
      // ETAPA 1: ASSINATURA DIGITAL DO PDF
      // =====================================================================
      let signedBase64 = documentBase64;

      if (provider === "fluxsigner") {
        // Assinatura digital com FluxSigner (certificado local)
        try {
          if (!certificateAlias) {
            toast.error("Nenhum certificado selecionado.");
            throw new Error("Certificado não selecionado");
          }

          const signResponse = await fluxSignerService.signPDFBase64(
            documentBase64 || "",
            certificateAlias,
            {
              reason: "Documento veterinário via FluxMed",
              location: "Brasil",
              useTimestamp: false,
            }
          );

          if (!signResponse?.signedPdfData) {
            throw new Error("Resposta de assinatura inválida");
          }

          signedBase64 = signResponse.signedPdfData;
          console.log("✅ PDF assinado com FluxSigner com sucesso");
        } catch (error) {
          console.error("Erro ao assinar PDF com FluxSigner:", error);
          toast.error("Erro ao assinar o documento com FluxSigner.");
          throw error;
        }
      } else {
        // Assinatura com VidaaS ou SafeID (servidor remoto)
        try {
          toast.info("Aguardando verificação e segurança do provedor...");

          const signResponse = await Base64SignPDF({
            pdfBase64: documentBase64 || "",
            cpfCnpj: user?.username || "",
            provider,
            userPassword: password,
            lifeTime: lifeTime || 0,
          });

          if (!signResponse?.length) {
            throw new Error("Resposta de assinatura inválida");
          }

          signedBase64 = signResponse?.replace(/\r\n/g, "");
          console.log("✅ PDF assinado com sucesso");
        } catch (error) {
          console.error("Erro ao assinar PDF:", error);
          toast.warn(
            "Não foi possível realizar a assinatura, por favor tente novamente!"
          );
          throw error;
        }
      }

      // =====================================================================
      // ETAPA 2: PERSISTÊNCIA DE RECURSOS FHIR
      // =====================================================================
      let basedOnRefs = "";

      // =====================================================================
      // TRATAMENTOS: Persiste MedicationRequest
      // =====================================================================
      if (currentDocumentType === "treatment") {
        try {
          // Busca os tratamentos correspondentes aos IDs
          const itemsToSign = treatments.filter((t: any) =>
            currentItemIds.includes(t.id)
          );

          // Adapta tratamentos para formato MedicationRequest
          const adaptedTreatments =
            adaptTreatmentsToMedicationFormat(itemsToSign);

          // Cria os MedicationRequest no servidor FHIR
          const medicationResponse = await PostMedicationRequest(
            adaptedTreatments,
            references
          );

          // Extrai referências dos MedicationRequests criados
          basedOnRefs =
            medicationResponse?.entry
              ?.map(
                (entry: any) =>
                  `<fhir:basedOn>
                  <fhir:reference value="${
                    entry.response?.location?.split("/_history")?.[0]
                  }"/>
                </fhir:basedOn>`
              )
              ?.join("") || "";

          console.log("✅ MedicationRequest criado com sucesso");
        } catch (error) {
          console.error("Erro ao criar MedicationRequest:", error);
          toast.warn("Erro ao registrar tratamentos no servidor.");
          throw error;
        }
      }

      // =====================================================================
      // EXAMES: Persiste ServiceRequest
      // =====================================================================
      else if (currentDocumentType === "exam") {
        try {
          // Busca os exames correspondentes aos IDs
          const itemsToSign = examsOrProcedures.filter((e: any) =>
            currentItemIds.includes(e.id)
          );

          // Adapta exames para formato ServiceRequest
          const adaptedExams = adaptExamsToServiceRequestFormat(itemsToSign);

          // Cria os ServiceRequest no servidor FHIR
          const serviceResponse = await PostServiceRequest(
            adaptedExams,
            references,
            [] // contained (vazio por enquanto)
          );

          // Extrai referências dos ServiceRequests criados
          basedOnRefs =
            serviceResponse?.entry
              ?.map(
                (entry: any) =>
                  `<fhir:basedOn>
                  <fhir:reference value="${
                    entry.response?.location?.split("/_history")?.[0]
                  }"/>
                </fhir:basedOn>`
              )
              ?.join("") || "";

          console.log("✅ ServiceRequest criado com sucesso");
        } catch (error) {
          console.error("Erro ao criar ServiceRequest:", error);
          toast.error("Erro ao registrar exames no servidor.");
          throw error;
        }
      }

      // =====================================================================
      // RELATÓRIOS: Não têm MedicationRequest/ServiceRequest
      // =====================================================================
      // Relatórios vão direto para DocumentReference (basedOnRefs vazio)

      // =====================================================================
      // ETAPA 3: PERSISTÊNCIA DO DOCUMENTREFERENCE COM PDF ASSINADO
      // =====================================================================
      try {
        // Define os metadados específicos de cada tipo de documento
        let codeValue = "";
        let codeDisplay = "";
        let categoryValue = "";
        let categoryDisplay = "";

        if (currentDocumentType === "treatment") {
          codeValue = "REC_SIM";
          codeDisplay = "Receituário Simples";
          categoryValue = "prescricao";
          categoryDisplay = "Receituário";
        } else if (currentDocumentType === "exam") {
          codeValue = "SERVICE_REQUEST";
          codeDisplay = "Solicitação de exame";
          categoryValue = "service-request";
          categoryDisplay = "Solicitação";
        } else if (currentDocumentType === "report") {
          codeValue = "AT";
          codeDisplay = "Atestado";
          categoryValue = "atestado";
          categoryDisplay = "Relatório";
        }

        // Persiste o DocumentReference no servidor FHIR
        // IMPORTANTE:
        // - Usa signedBase64 (PDF assinado digitalmente)
        // - Passa signed: true
        // - Passa o UUID do documento gerado
        // - Passa o idValue gerado no momento da criação do PDF
        console.log("📤 Enviando DocumentReference com idValue:", {
          documentUUID,
          documentIdValue,
          type: currentDocumentType,
        });

        await PostDocumentReference(
          {
            ...docReferenceData,
            base64: signedBase64, // ✅ PDF ASSINADO
            codeValue,
            codeDisplay,
            categoryValue,
            categoryDisplay,
          },
          references,
          basedOnRefs,
          true, // signed: true (documento assinado)
          documentUUID, // UUID do documento gerado
          documentIdValue // idValue gerado no momento da criação do PDF
        );

        console.log("✅ DocumentReference criado com sucesso (signed: true)");
        toast.success("Documento assinado e registrado com sucesso!");
      } catch (error) {
        console.error("Erro ao criar DocumentReference:", error);
        toast.error("Erro ao registrar documento assinado no servidor.");
        throw error;
      }

      // =====================================================================
      // ATUALIZAÇÃO DO ESTADO LOCAL (marca itens como assinados)
      // =====================================================================
      if (currentDocumentType === "treatment") {
        const updatedTreatments = treatments.map((t: any) =>
          currentItemIds.includes(t.id) ? { ...t, signed: true } : t
        );
        methods.setValue("treatments", updatedTreatments);
      } else if (currentDocumentType === "exam") {
        const updatedExams = examsOrProcedures.map((e: any) =>
          currentItemIds.includes(e.id) ? { ...e, signed: true } : e
        );
        methods.setValue("examsOrProcedures", updatedExams);
      } else if (currentDocumentType === "report") {
        const updatedReports = reports.map((r: any) =>
          currentItemIds.includes(r.id) ? { ...r, signed: true } : r
        );
        methods.setValue("reports", updatedReports);
      }

      // Salva o documento assinado na lista local
      const signedDocuments = methods.watch("signedDocuments") || [];
      const newSignedDocument = {
        uuid: documentUUID,
        title: documentTitle,
        filename: documentFilename,
        base64: documentBase64 || "",
        signedAt: new Date().toISOString(),
        type: currentDocumentType as
          | "treatment"
          | "exam"
          | "report"
          | "combined",
        itemIds: currentItemIds,
      };

      methods.setValue("signedDocuments", [
        ...signedDocuments,
        newSignedDocument,
      ]);

      console.log("✅ Documento assinado, persistido e itens atualizados!");
    } catch (error) {
      console.error("Erro ao assinar e persistir documento:", error);
      throw error; // Propaga o erro para o modal tratar
    }
  };

  /**
   * Busca a customização de background do PDF baseado no tipo de documento
   * Mapeia os tipos do sistema veterinário para os tipos de configuração:
   * - "treatment" -> "simple" (receituário simples)
   * - "exam" -> "exam" (solicitação de exame)
   * - "report" -> "certificate" (relatório/atestado)
   *
   * @param type - Tipo do documento veterinário ("treatment" | "exam" | "report")
   * @returns Objeto com backgroundImage ou undefined se não encontrado
   */
  async function getCustomization(type: "treatment" | "exam" | "report") {
    const response = await GetAdminQuestionnaireResponse({
      questionnaire: "customPdf",
      author: organization?.id,
      source: `Practitioner/${oids.cpf}-${user.username}`,
    });

    // Mapeia o tipo do documento veterinário para o tipo de configuração
    const configTypeMap: Record<"treatment" | "exam" | "report", string> = {
      treatment: "simple", // Receituário simples
      exam: "exam", // Solicitação de exame
      report: "certificate", // Relatório/atestado
    };

    const configType = configTypeMap[type];

    // Filtra o backgroundImage baseado no tipo de configuração
    const backgroundImage =
      response?.entry?.filter(
        (el: any) =>
          el.resource?.item?.[0]?.answer?.[0]?.valueString === configType
      )?.[0]?.resource?.item?.[2]?.answer?.[0]?.valueAttachment || undefined;

    return { backgroundImage };
  }

  /**
   * Gera documento individual de um item (apenas PDF, sem persistir)
   * A persistência ocorre apenas no handleSignDocument quando o usuário assinar
   */
  const handleGenerateIndividual = async (
    itemId: string,
    itemType: "treatment" | "exam" | "report"
  ) => {
    setLoading(true);
    setModalOpen(true);

    try {
      let result;
      let title = "";
      let filename = "";

      // Busca customização baseada no tipo de documento
      const customization = await getCustomization(itemType);
      const att = customization?.backgroundImage;
      const hasBackground = att?.data;

      // Monta additionalInfo: clínica + background (base64 + contentType)
      const additionalInfo = {
        ...clinicInfo,
        ...(hasBackground && {
          backgroundImage: normalizeBackgroundBase64(att.data),
          backgroundImageContentType: att.contentType || "image/png",
        }),
      };

      // =====================================================================
      // GERAÇÃO DO PDF (sem persistência)
      // =====================================================================
      if (itemType === "treatment") {
        const treatment = treatments.find((t: any) => t.id === itemId);
        if (!treatment) throw new Error("Tratamento não encontrado");

        title = `Receituário - ${treatment.name}`;
        filename = `receita_${treatment.name
          .toLowerCase()
          .replace(/\s/g, "_")}`;

        // Gera apenas o PDF do tratamento
        result = await generateTreatmentDocument(
          treatment,
          petInfo,
          tutorInfo,
          additionalInfo
        );
      } else if (itemType === "exam") {
        const exam = examsOrProcedures.find((e: any) => e.id === itemId);
        if (!exam) throw new Error("Exame não encontrado");

        title = `Solicitação de Exame - ${exam.description}`;
        filename = `exame_${exam.description
          .toLowerCase()
          .replace(/\s/g, "_")}`;

        // Gera apenas o PDF do exame
        result = await generateExamDocument(
          exam,
          petInfo,
          tutorInfo,
          additionalInfo
        );
      } else if (itemType === "report") {
        const report = reports.find((r: any) => r.id === itemId);
        if (!report) throw new Error("Relatório não encontrado");

        title = "Relatório de Atendimento";
        filename = "relatorio_atendimento";

        // Gera apenas o PDF do relatório
        result = await generateReportDocument(
          report,
          petInfo,
          tutorInfo,
          additionalInfo
        );
      }

      if (!result?.base64) {
        throw new Error("Erro ao gerar documento PDF");
      }

      // =====================================================================
      // ATUALIZAÇÃO DO ESTADO LOCAL (para exibir no modal)
      // =====================================================================
      setDocumentTitle(title);
      setDocumentFilename(filename);
      setDocumentBase64(result.base64);
      setDocumentUUID(result.uuid);
      setDocumentIdValue(result.idValue);

      // Define o tipo e IDs para posterior assinatura
      setCurrentDocumentType(itemType);
      setCurrentItemIds([itemId]);
    } catch (error) {
      console.error("Erro ao gerar documento:", error);
      toast.error("Erro ao gerar documento. Por favor, tente novamente.");
      setModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gera documento com todos os itens de uma seção (apenas PDF, sem persistir)
   * A persistência ocorre apenas no handleSignDocument quando o usuário assinar
   * Usa o mesmo modal (DocumentPreviewModal) que a geração individual
   */
  const handleGenerateSection = async (sectionType: string, items: any[]) => {
    setLoading(true);
    setModalOpen(true); // Abre o modal imediatamente

    try {
      let title = "";
      let filename = "";

      // Define título e nome do arquivo baseado no tipo de seção
      if (sectionType === "treatments") {
        title = "Receituário Completo";
        filename = "receituario_completo";
      } else if (sectionType === "exams") {
        title = "Solicitação de Exames e Procedimentos";
        filename = "solicitacao_exames";
      } else if (sectionType === "reports") {
        title = "Relatórios de Atendimento";
        filename = "relatorios";
      }

      // =====================================================================
      // GERAÇÃO DO PDF (sem persistência)
      // =====================================================================
      const docType =
        sectionType === "treatments"
          ? "treatment"
          : sectionType === "exams"
          ? "exam"
          : "report";

      // Busca customização baseada no tipo de documento
      const customization = await getCustomization(docType);
      const att = customization?.backgroundImage;
      const hasBackground = att?.data;
      const additionalInfo = {
        ...clinicInfo,
        ...(hasBackground && {
          backgroundImage: normalizeBackgroundBase64(att.data),
          backgroundImageContentType: att.contentType || "image/png",
        }),
      };

      const result = await generateSectionDocument(
        docType,
        items,
        petInfo,
        tutorInfo,
        additionalInfo
      );

      if (!result?.base64) {
        throw new Error("Erro ao gerar documento PDF");
      }

      // =====================================================================
      // ATUALIZAÇÃO DO ESTADO LOCAL (para exibir no modal)
      // =====================================================================
      setDocumentTitle(title);
      setDocumentFilename(filename);
      setDocumentBase64(result.base64);
      setDocumentUUID(result.uuid);
      setDocumentIdValue(result.idValue);
      setCurrentDocumentType(docType);
      setCurrentItemIds(items.map((item: any) => item.id));

      // Modal já está aberto, apenas carrega o documento
    } catch (error) {
      console.error("Erro ao gerar documento:", error);
      toast.warn("Erro ao gerar documento. Por favor, tente novamente.");
      setModalOpen(false); // Fecha o modal em caso de erro
    } finally {
      setLoading(false);
    }
  };

  // Se não há nenhum item (nem assinado nem não assinado), mostra estado vazio
  if (!hasAnyItems) {
    return (
      <Paper
        sx={{
          p: 3,
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <Description sx={{ fontSize: 40, color: "#bdbdbd" }} />
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
            Nenhum item disponível
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            Adicione tratamentos, exames ou relatórios na{" "}
            <strong>etapa anterior</strong> para poder gerar documentos aqui.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      {/* ========== GERAÇÃO DE NOVOS DOCUMENTOS ========== */}
      {hasUnsignedItems && (
        <Paper sx={{ p: 3, minHeight: 500 }}>
          <Stack spacing={2.5}>
            {/* Cabeçalho */}
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Gerar Novos Documentos
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Selecione os itens abaixo para gerar documentos
              </Typography>
            </Box>

            <Divider />

            {/* ========== SEÇÃO: TRATAMENTOS ========== */}
            {unsignedTreatments.length > 0 && (
              <Accordion
                sx={{
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  "&:before": { display: "none" },
                  borderRadius: "8px !important",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: "#fafafa",
                    borderRadius: "8px 8px 0 0",
                    minHeight: 56,
                    "&.Mui-expanded": { minHeight: 56 },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    width="100%"
                  >
                    <Medication sx={{ color: "#00A884", fontSize: 24 }} />
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ flexGrow: 1 }}
                    >
                      Tratamentos
                    </Typography>
                    <Chip
                      label={unsignedTreatments.length}
                      size="small"
                      sx={{
                        bgcolor: "#e8f5e9",
                        color: "#2e7d32",
                        fontWeight: 600,
                        mr: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateSection("treatments", unsignedTreatments);
                      }}
                      sx={{
                        textTransform: "none",
                        borderColor: "#00A884",
                        color: "#00A884",
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                        "&:hover": {
                          borderColor: "#008c6e",
                          bgcolor: "rgba(0, 168, 132, 0.08)",
                        },
                      }}
                    >
                      Gerar todos
                    </Button>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    {unsignedTreatments.map((treatment: any) => (
                      <Card
                        key={treatment.id}
                        variant="outlined"
                        sx={{
                          border: "1px solid #e0e0e0",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 2px 6px rgba(0, 168, 132, 0.1)",
                            borderColor: "#00A884",
                          },
                        }}
                      >
                        <CardContent
                          sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                          >
                            {/* Conteúdo compacto */}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                  color: "#00A884",
                                  mb: 0.3,
                                }}
                              >
                                {treatment.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  lineHeight: 1.4,
                                }}
                              >
                                {treatment.dosage}
                              </Typography>
                              {treatment.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    mt: 0.3,
                                    fontStyle: "italic",
                                    opacity: 0.8,
                                  }}
                                >
                                  {treatment.description}
                                </Typography>
                              )}
                            </Box>

                            {/* Botão gerar */}
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleGenerateIndividual(
                                  treatment.id,
                                  "treatment"
                                )
                              }
                              sx={{
                                bgcolor: "#00A884",
                                "&:hover": { bgcolor: "#008c6e" },
                                textTransform: "none",
                                minWidth: 80,
                                fontSize: "0.75rem",
                                py: 0.5,
                              }}
                            >
                              Gerar
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* ========== SEÇÃO: EXAMES/PROCEDIMENTOS ========== */}
            {unsignedExams.length > 0 && (
              <Accordion
                sx={{
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  "&:before": { display: "none" },
                  borderRadius: "8px !important",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: "#fafafa",
                    borderRadius: "8px 8px 0 0",
                    minHeight: 56,
                    "&.Mui-expanded": { minHeight: 56 },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    width="100%"
                  >
                    <Science sx={{ color: "#00A884", fontSize: 24 }} />
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ flexGrow: 1 }}
                    >
                      Exames/Procedimentos
                    </Typography>
                    <Chip
                      label={unsignedExams.length}
                      size="small"
                      sx={{
                        bgcolor: "#e3f2fd",
                        color: "#1565c0",
                        fontWeight: 600,
                        mr: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateSection("exams", unsignedExams);
                      }}
                      sx={{
                        textTransform: "none",
                        borderColor: "#00A884",
                        color: "#00A884",
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                        "&:hover": {
                          borderColor: "#008c6e",
                          bgcolor: "rgba(0, 168, 132, 0.08)",
                        },
                      }}
                    >
                      Gerar todos
                    </Button>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    {unsignedExams.map((exam: any) => (
                      <Card
                        key={exam.id}
                        variant="outlined"
                        sx={{
                          border: "1px solid #e0e0e0",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 2px 6px rgba(0, 168, 132, 0.1)",
                            borderColor: "#00A884",
                          },
                        }}
                      >
                        <CardContent
                          sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                          >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                  color: "#00A884",
                                  mb: 0.3,
                                }}
                              >
                                {exam.description}
                              </Typography>
                              {exam.notes && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    lineHeight: 1.4,
                                    fontStyle: "italic",
                                    opacity: 0.8,
                                  }}
                                >
                                  {exam.notes}
                                </Typography>
                              )}
                            </Box>

                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleGenerateIndividual(exam.id, "exam")
                              }
                              sx={{
                                bgcolor: "#00A884",
                                "&:hover": { bgcolor: "#008c6e" },
                                textTransform: "none",
                                minWidth: 80,
                                fontSize: "0.75rem",
                                py: 0.5,
                              }}
                            >
                              Gerar
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* ========== SEÇÃO: RELATÓRIOS ========== */}
            {unsignedReports.length > 0 && (
              <Accordion
                sx={{
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  "&:before": { display: "none" },
                  borderRadius: "8px !important",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: "#fafafa",
                    borderRadius: "8px 8px 0 0",
                    minHeight: 56,
                    "&.Mui-expanded": { minHeight: 56 },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    width="100%"
                  >
                    <Article sx={{ color: "#00A884", fontSize: 24 }} />
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ flexGrow: 1 }}
                    >
                      Relatórios
                    </Typography>
                    <Chip
                      label={unsignedReports.length}
                      size="small"
                      sx={{
                        bgcolor: "#fff3e0",
                        color: "#e65100",
                        fontWeight: 600,
                        mr: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateSection("reports", unsignedReports);
                      }}
                      sx={{
                        textTransform: "none",
                        borderColor: "#00A884",
                        color: "#00A884",
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                        "&:hover": {
                          borderColor: "#008c6e",
                          bgcolor: "rgba(0, 168, 132, 0.08)",
                        },
                      }}
                    >
                      Gerar todos
                    </Button>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    {unsignedReports.map((report: any) => (
                      <Card
                        key={report.id}
                        variant="outlined"
                        sx={{
                          border: "1px solid #e0e0e0",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 2px 6px rgba(0, 168, 132, 0.1)",
                            borderColor: "#00A884",
                          },
                        }}
                      >
                        <CardContent
                          sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                          >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                  color: "#00A884",
                                  mb: 0.5,
                                }}
                              >
                                Relatório Completo
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.4,
                                }}
                              >
                                {report.content}
                              </Typography>
                            </Box>

                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleGenerateIndividual(report.id, "report")
                              }
                              sx={{
                                bgcolor: "#00A884",
                                "&:hover": { bgcolor: "#008c6e" },
                                textTransform: "none",
                                minWidth: 80,
                                fontSize: "0.75rem",
                                py: 0.5,
                              }}
                            >
                              Gerar
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </Paper>
      )}

      {/* ========== LISTA DE DOCUMENTOS ASSINADOS ========== */}
      <SignedDocumentsList />

      {/* ========== MODAL DE VISUALIZAÇÃO E ASSINATURA (ÚNICO PARA TUDO) ========== */}

      {modalOpen && (
        <DocumentPreviewModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            // Reseta os dados do documento ao fechar
            setDocumentBase64(null);
            setDocumentUUID("");
            setDocumentTitle("");
            setDocumentFilename("");
            setCurrentDocumentType("");
            setCurrentItemIds([]);
          }}
          documentBase64={documentBase64}
          documentTitle={documentTitle}
          documentFilename={documentFilename}
          documentUUID={documentUUID}
          loading={loading}
          onSign={handleSignDocument}
        />
      )}
    </>
  );
};

export default SignatureStep;
